import { initializeApp, getApps } from "firebase/app";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  runTransaction,
  collectionGroup,
  limit,
} from "firebase/firestore";
import { convertImageToWebPBase64 } from "../utils/imageConverter";

// Firebase configuration (same project)
const firebaseConfig = {
  apiKey: "AIzaSyCPsPJbDoTGLw1sWwzjC2G03COD3c2Mz5Q",
  authDomain: "putri-henna.firebaseapp.com",
  projectId: "putri-henna",
  storageBucket: "putri-henna.firebasestorage.app",
  messagingSenderId: "167568256964",
  appId: "1:167568256964:web:c0d8f9bce9550a4e16be91",
  measurementId: "G-NZS2M4HVCP",
};

// Initialize Firestore (prevent duplicate app initialization)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const fs = getFirestore(app);

// ============================================================================
// MIGRATION HELPERS - Gallery image lookup map
// ============================================================================

// Create a map for client-side resolution of gallery images
const galleryImageByUrl = new Map();

// Subscribe to gallery to build the map
let galleryMapSubscribed = false;
export const subscribeToGalleryMap = (callback, mapCallback) => {
  const unsubscribeGallery = subscribeToGallery((data) => {
    const map = new Map(data.map((item) => [item.id, item]));
    
    if (mapCallback) {
      mapCallback(map);
    }
    
    if (!galleryMapSubscribed && callback) {
      callback(data);
      galleryMapSubscribed = true;
    }
    
    return () => {
      if (unsubscribeGallery) unsubscribeGallery();
    };
  });
  
  return unsubscribeGallery;
};

export const getGalleryImageMap = () => {
  // This will be populated by subscribeToGalleryMap
  // For immediate use, we need to fetch gallery items separately
  return new Map();
};

// ============================================================================
// CATEGORIES
// ============================================================================

export const getCategories = async () => {
  const q = query(collection(fs, "categories"), orderBy("displayOrder", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToCategories = (callback) => {
  const q = query(collection(fs, "categories"), orderBy("displayOrder", "asc"));
  return onSnapshot(q, (snapshot) => {
    const categories = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(categories);
  });
};

export const createCategory = async (categoryData) => {
  // Get highest displayOrder
  const q = query(
    collection(fs, "categories"),
    orderBy("displayOrder", "desc")
  );
  const snapshot = await getDocs(q);
  const nextOrder = snapshot.empty
    ? 1
    : (snapshot.docs[0].data().displayOrder || 0) + 1;

  const categoryRef = doc(collection(fs, "categories"));
  const newCategory = {
    id: categoryRef.id,
    name: categoryData.name,
    description: categoryData.description || null,
    displayOrder: nextOrder,
    isActive: categoryData.isActive !== undefined ? categoryData.isActive : true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(categoryRef, newCategory);
  return { id: categoryRef.id, ...newCategory };
};

export const updateCategory = async (categoryId, updates) => {
  const categoryRef = doc(fs, "categories", categoryId);
  await updateDoc(categoryRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteCategory = async (categoryId) => {
  const categoryRef = doc(fs, "categories", categoryId);
  await deleteDoc(categoryRef);
};

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

export const hasCategories = async () => {
  const q = query(collection(fs, "categories"), limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

export const hasPackages = async () => {
  const q = query(collection(fs, "packages"), limit(1));
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

export const migrateFromMock = async (mockData) => {
  const results = {
    categories: { created: 0, errors: [] },
    packages: { created: 0, errors: [] },
  };

  try {
    // Migrate Categories
    for (const service of mockData.services) {
      try {
        const categoryRef = doc(collection(fs, "categories"));
        const categoryData = {
          id: categoryRef.id,
          name: service.category,
          description: null,
          displayOrder: service.id || 1,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        await setDoc(categoryRef, categoryData);
        results.categories.created++;

        // Migrate Packages for this category
        if (service.packages) {
          for (const pkg of service.packages) {
            try {
              await migratePackage(pkg, categoryRef.id, service.category);
              results.packages.created++;
            } catch (error) {
              console.error(`Error migrating package ${pkg.name}:`, error);
              results.packages.errors.push(pkg.name);
            }
          }
        }

        // Migrate Subcategories and their packages
        if (service.subcategories) {
          for (const subcategory of service.subcategories) {
            if (subcategory.packages) {
              for (const pkg of subcategory.packages) {
                try {
                  await migratePackage(pkg, categoryRef.id, service.category, subcategory.name);
                  results.packages.created++;
                } catch (error) {
                  console.error(`Error migrating package ${pkg.name}:`, error);
                  results.packages.errors.push(pkg.name);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error(`Error migrating category ${service.category}:`, error);
        results.categories.errors.push(service.category);
      }
    }

    return results;
  } catch (error) {
    console.error("Migration error:", error);
    throw error;
  }
};

const migratePackage = async (pkg, categoryId, categoryName, subcategoryName = null) => {
  const packageRef = doc(collection(fs, "packages"));

  // Get next display order
  const q = query(
    collection(fs, "packages"),
    where("categoryId", "==", categoryId),
    orderBy("displayOrder", "desc")
  );
  const snapshot = await getDocs(q);
  const nextOrder = snapshot.empty ? 1 : (snapshot.docs[0].data().displayOrder || 0) + 1;

  // Migration logic: convert images to galleryImageIds if possible
  let galleryImageIds = [];
  let images = [];
  let image = null;

  // Try to match images to existing gallery items
  if (pkg.images && Array.isArray(pkg.images) && pkg.images.length > 0) {
    const matchedGalleryIds = pkg.images
      .map((url) => galleryImageByUrl.get(url))
      .filter(Boolean);
    galleryImageIds = Array.from(new Set(matchedGalleryIds));
    images = pkg.images;
  } else if (pkg.image) {
    const matchedGalleryId = galleryImageByUrl.get(pkg.image);
    if (matchedGalleryId) {
      galleryImageIds = [matchedGalleryId];
      images = [];
    } else {
      images = [pkg.image];
    }
  }

  const packageData = {
    id: packageRef.id,
    name: pkg.name,
    description: pkg.features?.join("\n") || null,
    categoryId: categoryId,
    subcategoryName: subcategoryName,
    categoryNameSnapshot: categoryName,
    price: pkg.price || 0,
    duration: null,
    includes: pkg.features || [],
    image: image,
    images: images,
    galleryImageIds: galleryImageIds,
    displayOrder: nextOrder,
    isActive: true,
    popular: pkg.popular || false,
    type: pkg.type || null,
    additionalService: pkg.additionalService || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(packageRef, packageData);
  return packageRef.id;
};

// ============================================================================
// PACKAGES
// ============================================================================

export const getPackages = async (categoryId = null) => {
  let q = query(collection(fs, "packages"), orderBy("displayOrder", "asc"));

  if (categoryId) {
    q = query(
      collection(fs, "packages"),
      where("categoryId", "==", categoryId),
      orderBy("displayOrder", "asc")
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getPackageById = async (packageId) => {
  const docRef = doc(fs, "packages", packageId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const subscribeToPackages = (callback, categoryId = null) => {
  let q = query(collection(fs, "packages"), orderBy("displayOrder", "asc"));

  if (categoryId) {
    q = query(
      collection(fs, "packages"),
      where("categoryId", "==", categoryId),
      orderBy("displayOrder", "asc")
    );
  }

  return onSnapshot(q, (snapshot) => {
    const packages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(packages);
  });
};

// ============================================================================
// ADD-ONS
// ============================================================================

export const getAddOns = async () => {
  const q = query(collection(fs, "add_ons"), where("isActive", "==", true));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getAddOnsByIds = async (addOnIds) => {
  if (!addOnIds || addOnIds.length === 0) return [];
  const q = query(
    collection(fs, "add_ons"),
    where("isActive", "==", true),
    where("__name__", "in", addOnIds)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToAddOns = (callback) => {
  const q = query(collection(fs, "add_ons"), where("isActive", "==", true));
  return onSnapshot(q, (snapshot) => {
    const addOns = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(addOns);
  });
};

/**
 * Get gallery image map for resolving IDs to URLs
 */
const getGalleryMap = () => {
  // This should be populated by the app using galleryItems from Firebase
  // For now, return empty map - caller should provide galleryItems
  return new Map();
};

export const createPackage = async (packageData, galleryItems = []) => {
  // Get highest displayOrder
  const q = query(
    collection(fs, "packages"),
    where("categoryId", "==", packageData.categoryId),
    orderBy("displayOrder", "desc")
  );
  const snapshot = await getDocs(q);
  const nextOrder = snapshot.empty
    ? 1
    : (snapshot.docs[0].data().displayOrder || 0) + 1;

  const packageRef = doc(collection(fs, "packages"));
  
  // Create gallery image map for resolution
  const galleryMap = new Map(galleryItems.map((item) => [item.id, item]));
  
  const newPackage = {
    id: packageRef.id,
    name: packageData.name,
    description: packageData.description || null,
    categoryId: packageData.categoryId,
    subcategoryName: packageData.subcategoryName || null,
    price: packageData.price || 0,
    duration: packageData.duration || null,
    includes: packageData.includes || [],
    image: packageData.image || null,
    images: Array.isArray(packageData.images)
      ? packageData.images
      : packageData.image
      ? [packageData.image]
      : [],
    galleryImageIds: Array.isArray(packageData.galleryImageIds)
      ? packageData.galleryImageIds.filter(Boolean)
      : [],
    availableAddOns: packageData.availableAddOns || [],
    displayOrder: nextOrder,
    isActive: packageData.isActive !== undefined ? packageData.isActive : true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(packageRef, newPackage);
  return { id: packageRef.id, ...newPackage };
};

export const updatePackage = async (packageId, updates, galleryItems = []) => {
  const packageRef = doc(fs, "packages", packageId);
  
  // Create gallery image map for resolution
  const galleryMap = new Map(galleryItems.map((item) => [item.id, item]));
  
  const updatedData = {
    ...updates,
    updatedAt: serverTimestamp(),
  };

  await updateDoc(packageRef, updatedData);
  return { id: packageRef.id, ...updatedData };
};

export const deletePackage = async (packageId) => {
  const packageRef = doc(fs, "packages", packageId);
  await deleteDoc(packageRef);
};

export const createAddOn = async (addOnData) => {
  const addOnRef = doc(collection(fs, "add_ons"));
  const newAddOn = {
    id: addOnRef.id,
    name: addOnData.name,
    description: addOnData.description || null,
    price: addOnData.price || 0,
    isActive: addOnData.isActive !== undefined ? addOnData.isActive : true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(addOnRef, newAddOn);
  return { id: addOnRef.id, ...newAddOn };
};

export const updateAddOn = async (addOnId, updates) => {
  const addOnRef = doc(fs, "add_ons", addOnId);
  await updateDoc(addOnRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteAddOn = async (addOnId) => {
  const addOnRef = doc(fs, "add_ons", addOnId);
  await deleteDoc(addOnRef);
};

export const toggleAddOnStatus = async (addOnId, isActive) => {
  const addOnRef = doc(fs, "add_ons", addOnId);
  await updateDoc(addOnRef, {
    isActive,
    updatedAt: serverTimestamp(),
  });
};

// ============================================================================
// MIGRATION: Convert add-ons from category-based to package-based
// ============================================================================

export const migrateAddOnsToPackageModel = async () => {
  try {
    const result = { addOnsMigrated: 0, packagesUpdated: 0, errors: [] };

    // 1. Get all add-ons with categoryApplicability
    const addOnsSnapshot = await getDocs(collection(fs, "add_ons"));
    const oldAddOns = addOnsSnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((a) => a.categoryApplicability && a.categoryApplicability.length > 0);

    if (oldAddOns.length === 0) {
      return { ...result, message: "Tidak ada add-on dengan categoryApplicability ditemukan." };
    }

    // 2. Get all packages
    const packagesSnapshot = await getDocs(collection(fs, "packages"));
    const allPackages = packagesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // 3. For each old add-on, find matching packages and add to their availableAddOns
    for (const addOn of oldAddOns) {
      const matchingPackages = allPackages.filter((pkg) =>
        addOn.categoryApplicability.includes(pkg.categoryId)
      );

      for (const pkg of matchingPackages) {
        const currentAddOns = pkg.availableAddOns || [];
        if (!currentAddOns.includes(addOn.id)) {
          currentAddOns.push(addOn.id);
          await updateDoc(doc(fs, "packages", pkg.id), {
            availableAddOns: currentAddOns,
            updatedAt: serverTimestamp(),
          });
          result.packagesUpdated++;
        }
      }

      // 4. Remove categoryApplicability from add-on
      await updateDoc(doc(fs, "add_ons", addOn.id), {
        categoryApplicability: [],
        updatedAt: serverTimestamp(),
      });
      result.addOnsMigrated++;
    }

    return {
      ...result,
      message: `Migrasi selesai: ${result.addOnsMigrated} add-on dimigrasi, ${result.packagesUpdated} paket diperbarui.`,
    };
  } catch (error) {
    console.error("Migration error:", error);
    throw error;
  }
};

// ============================================================================
// BOOKINGS
// ============================================================================

export const createBooking = async (bookingData) => {
  // Generate booking number
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const q = query(
    collection(fs, "bookings"),
    where("bookingNumber", ">=", `BK-${date}`),
    orderBy("bookingNumber", "desc")
  );
  const snapshot = await getDocs(q);

  let seq = 1;
  if (!snapshot.empty) {
    const lastNum = snapshot.docs[0].data().bookingNumber;
    seq = parseInt(lastNum.split("-")[2]) + 1;
  }

  const bookingNumber = `BK-${date}-${String(seq).padStart(4, "0")}`;

  // Calculate totals
  const totalAmount = bookingData.items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const depositAmount = totalAmount * ((bookingData.depositPercent || 30) / 100);

  // Create booking with transaction
  const bookingRef = doc(collection(fs, "bookings"));

  await runTransaction(fs, async (transaction) => {
    // Check availability - same henna date AND same henna time
    const conflictsQuery = query(
      collection(fs, "bookings"),
      where("hennaDate", "==", bookingData.hennaDate),
      where("hennaTime", "==", bookingData.hennaTime || null),
      where("status", "not-in", ["CANCELLED", "NO_SHOW"])
    );
    const conflicts = await getDocs(conflictsQuery);

    if (!conflicts.empty) {
      throw new Error("Sudah ada booking pada tanggal dan jam proses henna tersebut");
    }

    // Create booking document
    const newBooking = {
      id: bookingRef.id,
      bookingNumber,
      clientId: bookingData.clientId || null,
      clientName: bookingData.clientName,
      clientPhone: bookingData.clientPhone,
      clientEmail: bookingData.clientEmail,
      clientInstagram: bookingData.clientInstagram || null,
      eventDate: bookingData.eventDate,
      eventTime: bookingData.eventTime || null,
      eventLocation: bookingData.eventLocation || null,
      eventType: bookingData.eventType,
      hennaDate: bookingData.hennaDate,
      hennaTime: bookingData.hennaTime || null,
      status: "PENDING_DEPOSIT",
      depositPercent: bookingData.depositPercent || 30,
      depositAmount,
      totalAmount,
      paidAmount: 0,
      remainingAmount: totalAmount,
      discountAmount: 0,
      specialRequests: bookingData.specialRequests || null,
      source: bookingData.source || "website",
      createdBy: bookingData.createdBy || "user",
      itemCount: bookingData.items.length,
      paymentCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    transaction.set(bookingRef, newBooking);

    // Create booking items
    bookingData.items.forEach((item, index) => {
      const itemRef = doc(collection(bookingRef, "items"));
      const itemData = {
        id: itemRef.id,
        itemType: item.type,
        packageId: item.packageId || null,
        packageSnapshot: item.packageSnapshot || null,
        addOnSnapshot: item.addOnSnapshot || null,
        priceSnapshot: item.price,
        quantity: item.quantity,
        subtotal: item.price * item.quantity,
        addOnDetails: item.addOnDetails || null,
        displayOrder: index + 1,
        createdAt: serverTimestamp(),
      };
      transaction.set(itemRef, itemData);
    });

    // Update/create client
    if (bookingData.clientId) {
      const clientRef = doc(fs, "clients", bookingData.clientId);
      transaction.update(clientRef, {
        totalBookings: increment(1),
        lastBookingDate: bookingData.eventDate,
        updatedAt: serverTimestamp(),
      });
    }
  });

  return { bookingId: bookingRef.id, bookingNumber, depositAmount };
};

export const getBookings = async (filters = {}) => {
  let q = collection(fs, "bookings");

  // Apply filters
  if (filters.clientId) {
    q = query(q, where("clientId", "==", filters.clientId));
  }
  if (filters.status) {
    q = query(q, where("status", "==", filters.status));
  }
  if (filters.startDate) {
    q = query(q, where("eventDate", ">=", filters.startDate));
  }
  if (filters.endDate) {
    q = query(q, where("eventDate", "<=", filters.endDate));
  }

  q = query(q, orderBy("eventDate", "desc"));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const subscribeToBookings = (callback, filters = {}) => {
  let q = collection(fs, "bookings");

  // Apply filters
  if (filters.clientId) {
    q = query(q, where("clientId", "==", filters.clientId));
  }
  if (filters.status) {
    q = query(q, where("status", "==", filters.status));
  }

  q = query(q, orderBy("createdAt", "desc"));

  return onSnapshot(q, async (snapshot) => {
    const bookings = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        const booking = { id: docSnap.id, ...docSnap.data() };

        // Get items for each booking
        const itemsSnap = await getDocs(
          query(collection(docSnap.ref, "items"), orderBy("displayOrder", "asc"))
        );
        booking.items = itemsSnap.docs.map((itemDoc) => ({
          id: itemDoc.id,
          ...itemDoc.data(),
        }));

        return booking;
      })
    );
    callback(bookings);
  });
};

export const getBookingById = async (bookingId) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  const bookingSnap = await getDoc(bookingRef);

  if (!bookingSnap.exists()) {
    return null;
  }

  const booking = { id: bookingSnap.id, ...bookingSnap.data() };

  // Get items
  const itemsSnap = await getDocs(
    query(collection(bookingRef, "items"), orderBy("displayOrder", "asc"))
  );
  const items = itemsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  // Get payments
  const paymentsSnap = await getDocs(
    query(collection(bookingRef, "payments"), orderBy("createdAt", "desc"))
  );
  const payments = paymentsSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return { ...booking, items, payments };
};

export const updateBooking = async (bookingId, updates) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  await updateDoc(bookingRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const cancelBooking = async (bookingId, reason) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  await updateDoc(bookingRef, {
    status: "CANCELLED",
    cancellationReason: reason,
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const markBookingCompleted = async (bookingId) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  await updateDoc(bookingRef, {
    status: "COMPLETED",
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const deleteBooking = async (bookingId) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  await deleteDoc(bookingRef);
};

// ============================================================================
// BOOKING ITEMS
// ============================================================================

export const addBookingItem = async (bookingId, itemData) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  const itemRef = doc(collection(bookingRef, "items"));

  const newItem = {
    id: itemRef.id,
    itemType: itemData.type,
    packageId: itemData.packageId || null,
    packageSnapshot: itemData.packageSnapshot || null,
    addOnSnapshot: itemData.addOnSnapshot || null,
    priceSnapshot: itemData.price,
    quantity: itemData.quantity,
    subtotal: itemData.price * itemData.quantity,
    addOnDetails: itemData.addOnDetails || null,
    createdAt: serverTimestamp(),
  };

  await runTransaction(fs, async (transaction) => {
    transaction.set(itemRef, newItem);

    // Update booking totals
    const bookingSnap = await getDoc(bookingRef);
    const booking = bookingSnap.data();
    const newTotal = booking.totalAmount + newItem.subtotal;
    const newRemaining = newTotal - booking.paidAmount;

    transaction.update(bookingRef, {
      totalAmount: newTotal,
      remainingAmount: newRemaining,
      itemCount: increment(1),
      updatedAt: serverTimestamp(),
    });
  });

  return newItem;
};

// ============================================================================
// PAYMENTS
// ============================================================================

export const uploadPaymentProof = async (bookingId, paymentData) => {
  const bookingRef = doc(fs, "bookings", bookingId);

  // Generate payment number
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const q = query(
    collectionGroup(fs, "payments"),
    where("paymentNumber", ">=", `PAY-${date}`),
    orderBy("paymentNumber", "desc")
  );
  const snapshot = await getDocs(q);

  let seq = 1;
  if (!snapshot.empty) {
    const lastNum = snapshot.docs[0].data().paymentNumber;
    seq = parseInt(lastNum.split("-")[2]) + 1;
  }

  const paymentNumber = `PAY-${date}-${String(seq).padStart(4, "0")}`;
  const paymentRef = doc(collection(bookingRef, "payments"));

  const newPayment = {
    id: paymentRef.id,
    paymentNumber,
    paymentType: paymentData.paymentType || "DEPOSIT",
    amount: paymentData.amount,
    paymentMethod: paymentData.paymentMethod,
    paymentChannel: paymentData.paymentChannel || null,
    transactionReference: paymentData.transactionReference || null,
    paymentDate: paymentData.paymentDate,
    proofImage: paymentData.proofImage || null,
    status: "PENDING",
    notes: paymentData.notes || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(paymentRef, newPayment);

  return { paymentId: paymentRef.id, paymentNumber };
};

export const verifyPayment = async (bookingId, paymentId, adminId) => {
  const bookingRef = doc(fs, "bookings", bookingId);
  const paymentRef = doc(bookingRef, "payments", paymentId);

  await runTransaction(fs, async (transaction) => {
    const paymentSnap = await getDoc(paymentRef);
    const bookingSnap = await getDoc(bookingRef);

    if (!paymentSnap.exists() || !bookingSnap.exists()) {
      throw new Error("Document not found");
    }

    const payment = paymentSnap.data();
    const booking = bookingSnap.data();

    // Update payment status
    transaction.update(paymentRef, {
      status: "VERIFIED",
      verifiedBy: adminId,
      verifiedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Get all verified payments
    const paymentsSnap = await getDocs(
      query(collection(bookingRef, "payments"), where("status", "==", "VERIFIED"))
    );

    const totalPaid = paymentsSnap.docs.reduce(
      (sum, doc) => sum + doc.data().amount,
      0
    );

    const remainingAmount = booking.totalAmount - totalPaid;
    const newStatus =
      totalPaid >= booking.depositAmount ? "CONFIRMED" : "PENDING_DEPOSIT";

    transaction.update(bookingRef, {
      paidAmount: totalPaid,
      remainingAmount,
      status: newStatus,
      paymentCount: paymentsSnap.size,
      confirmedAt:
        newStatus === "CONFIRMED"
          ? serverTimestamp()
          : booking.confirmedAt || null,
      updatedAt: serverTimestamp(),
    });

    // Update client total spent
    if (booking.clientId) {
      const clientRef = doc(fs, "clients", booking.clientId);
      transaction.update(clientRef, {
        totalSpent: increment(totalPaid - booking.paidAmount),
        updatedAt: serverTimestamp(),
      });
    }
  });
};

export const rejectPayment = async (bookingId, paymentId, reason) => {
  const paymentRef = doc(fs, "bookings", bookingId, "payments", paymentId);
  await updateDoc(paymentRef, {
    status: "REJECTED",
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });
};

export const subscribeToPendingPayments = (callback) => {
  const q = query(
    collectionGroup(fs, "payments"),
    where("status", "==", "PENDING"),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const payments = [];
    snapshot.docs.forEach((doc) => {
      const payment = { id: doc.id, ...doc.data() };
      // Get booking info from parent path
      const bookingId = doc.ref.parent.parent.id;
      payment.bookingId = bookingId;
      payments.push(payment);
    });
    callback(payments);
  });
};

export const addPayment = async (bookingId, paymentData) => {
  const bookingRef = doc(fs, "bookings", bookingId);

  // Generate payment number
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const q = query(
    collectionGroup(fs, "payments"),
    where("paymentNumber", ">=", `PAY-${date}`),
    orderBy("paymentNumber", "desc")
  );
  const snapshot = await getDocs(q);

  let seq = 1;
  if (!snapshot.empty) {
    const lastNum = snapshot.docs[0].data().paymentNumber;
    seq = parseInt(lastNum.split("-")[2]) + 1;
  }

  const paymentNumber = `PAY-${date}-${String(seq).padStart(4, "0")}`;
  const paymentRef = doc(collection(bookingRef, "payments"));

  await runTransaction(fs, async (transaction) => {
    const bookingSnap = await getDoc(bookingRef);

    if (!bookingSnap.exists()) {
      throw new Error("Booking not found");
    }

    const booking = bookingSnap.data();

    // Create payment document
    const newPayment = {
      id: paymentRef.id,
      paymentNumber,
      paymentType: paymentData.paymentType || "DEPOSIT",
      amount: paymentData.amount,
      paymentMethod: paymentData.paymentMethod,
      paymentChannel: paymentData.paymentChannel || null,
      transactionReference: paymentData.transactionReference || null,
      status: paymentData.status || "VERIFIED",
      notes: paymentData.notes || null,
      verifiedBy: paymentData.verifiedBy || null,
      verifiedAt: paymentData.status === "VERIFIED" ? serverTimestamp() : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    transaction.set(paymentRef, newPayment);

    // Update booking if payment is verified
    if (newPayment.status === "VERIFIED") {
      const newPaidAmount = booking.paidAmount + paymentData.amount;
      const newRemainingAmount = booking.totalAmount - newPaidAmount;

      // Get all verified payments count
      const paymentsSnap = await getDocs(
        query(collection(bookingRef, "payments"), where("status", "==", "VERIFIED"))
      );

      const newStatus =
        newPaidAmount >= booking.depositAmount ? "CONFIRMED" : "PENDING_DEPOSIT";

      transaction.update(bookingRef, {
        paidAmount: newPaidAmount,
        remainingAmount: newRemainingAmount,
        status: newStatus,
        paymentCount: paymentsSnap.size + 1,
        confirmedAt:
          newStatus === "CONFIRMED" && booking.status !== "CONFIRMED"
            ? serverTimestamp()
            : booking.confirmedAt || null,
        updatedAt: serverTimestamp(),
      });

      // Update client total spent
      if (booking.clientId) {
        const clientRef = doc(fs, "clients", booking.clientId);
        transaction.update(clientRef, {
          totalSpent: increment(paymentData.amount),
          updatedAt: serverTimestamp(),
        });
      }
    }
  });

  return { paymentId: paymentRef.id, paymentNumber };
};

// ============================================================================
// CLIENTS
// ============================================================================

export const getOrCreateClient = async (clientData) => {
  // Check if client exists by email
  const q = query(
    collection(fs, "clients"),
    where("email", "==", clientData.email)
  );
  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    const existingClient = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    return existingClient;
  }

  // Create new client
  const clientRef = doc(collection(fs, "clients"));
  const newClient = {
    id: clientRef.id,
    name: clientData.name,
    email: clientData.email,
    phone: clientData.phone,
    phoneFormatted: clientData.phone.replace(/[^\d]/g, ""), // remove +, spaces, dashes
    instagram: clientData.instagram || null,
    address: clientData.address || null,
    notes: clientData.notes || null,
    totalBookings: 0,
    totalSpent: 0,
    isRepeatClient: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(clientRef, newClient);

  return { id: clientRef.id, ...newClient };
};

export const getClientBookings = async (clientId) => {
  const q = query(
    collection(fs, "bookings"),
    where("clientId", "==", clientId),
    orderBy("eventDate", "desc")
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// ============================================================================
// IMAGE UPLOAD (Base64 with WebP conversion)
// ============================================================================

/**
 * Upload image as WebP base64 to Firestore
 * @param {File} file - The image file to upload
 * @param {Object} options - Conversion options
 * @returns {Promise<Object>} - Object containing base64 data and metadata
 */
export const uploadImage = async (file, options = {}) => {
  try {
    const result = await convertImageToWebPBase64(file, options);
    return {
      base64: result.base64,
      dataUrl: result.dataUrl,
      width: result.width,
      height: result.height,
      size: result.size,
      format: result.format,
    };
  } catch (error) {
    console.error('Image upload failed:', error);
    throw error;
  }
};

/**
 * Upload payment proof image as WebP base64
 * @param {File} file - The payment proof image file
 * @returns {Promise<Object>} - Upload result with metadata
 */
export const uploadPaymentProofImage = async (file) => {
  const result = await uploadImage(file, { maxWidth: 1280, maxHeight: 1280, quality: 80 });
  return {
    ...result,
    uploadedAt: new Date().toISOString(),
    type: 'payment-proof',
  };
};

/**
 * Upload gallery image as WebP base64
 * @param {File} file - The gallery image file
 * @returns {Promise<Object>} - Upload result with metadata
 */
export const uploadGalleryImage = async (file) => {
  const result = await uploadImage(file, { maxWidth: 1920, maxHeight: 1920, quality: 85 });
  return {
    ...result,
    uploadedAt: new Date().toISOString(),
    type: 'gallery',
  };
};

/**
 * Upload testimonial image as WebP base64
 * @param {File} file - The testimonial image file
 * @returns {Promise<Object>} - Upload result with metadata
 */
export const uploadTestimonialImage = async (file) => {
  const result = await uploadImage(file, { maxWidth: 1280, maxHeight: 1280, quality: 85 });
  return {
    ...result,
    uploadedAt: new Date().toISOString(),
    type: 'testimonial',
  };
};

// ============================================================================
// SCHEDULE & AVAILABILITY
// ============================================================================

// Get all schedules for a date range
export const getSchedules = async (startDate, endDate) => {
  const schedulesRef = collection(fs, "schedules");
  const q = query(
    schedulesRef,
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Subscribe to schedules for real-time updates
export const subscribeToSchedules = (callback, startDate = null, endDate = null) => {
  let q;
  const schedulesRef = collection(fs, "schedules");

  if (startDate && endDate) {
    q = query(
      schedulesRef,
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "asc")
    );
  } else {
    q = query(schedulesRef, orderBy("date", "asc"));
  }

  return onSnapshot(q, (snapshot) => {
    const schedules = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(schedules);
  });
};

// Create or update schedule
export const upsertSchedule = async (scheduleData) => {
  if (scheduleData.id) {
    // Update existing
    const scheduleRef = doc(fs, "schedules", scheduleData.id);
    await updateDoc(scheduleRef, {
      ...scheduleData,
      updatedAt: serverTimestamp(),
    });
    return { id: scheduleData.id, ...scheduleData };
  } else {
    // Create new
    const scheduleRef = doc(collection(fs, "schedules"));
    const newSchedule = {
      date: scheduleData.date,
      timeSlots: scheduleData.timeSlots || [],
      isAvailable: scheduleData.isAvailable !== undefined ? scheduleData.isAvailable : true,
      notes: scheduleData.notes || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    await setDoc(scheduleRef, newSchedule);
    return { id: scheduleRef.id, ...newSchedule };
  }
};

// Delete schedule
export const deleteSchedule = async (scheduleId) => {
  const scheduleRef = doc(fs, "schedules", scheduleId);
  await deleteDoc(scheduleRef);
};

// Get schedule for a specific date
export const getScheduleByDate = async (date) => {
  const q = query(collection(fs, "schedules"), where("date", "==", date));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
};

// Check for booking conflicts
export const checkBookingConflict = async (date, time, duration = 2) => {
  // Convert time to minutes for comparison
  const [hours, minutes] = time.split(':').map(Number);
  const startTimeMinutes = hours * 60 + minutes;
  const endTimeMinutes = startTimeMinutes + (duration * 60);

  // Get all bookings for the date
  const bookingsRef = collection(fs, "bookings");
  const q = query(
    bookingsRef,
    where("eventDate", "==", date)
  );

  const snapshot = await getDocs(q);
  const bookings = snapshot.docs.map((doc) => doc.data());

  // Check for conflicts
  const conflicts = [];
  for (const booking of bookings) {
    // Skip cancelled or completed bookings
    if (booking.status === "CANCELLED" || booking.status === "COMPLETED" || booking.status === "NO_SHOW") {
      continue;
    }

    if (booking.eventTime) {
      const [bHours, bMinutes] = booking.eventTime.split(':').map(Number);
      const bStartTimeMinutes = bHours * 60 + bMinutes;
      // Estimate booking duration (can be improved by storing duration in booking)
      const bDuration = booking.estimatedDuration || 3; // Default 3 hours
      const bEndTimeMinutes = bStartTimeMinutes + (bDuration * 60);

      // Check for overlap
      const hasOverlap = (
        (startTimeMinutes >= bStartTimeMinutes && startTimeMinutes < bEndTimeMinutes) ||
        (endTimeMinutes > bStartTimeMinutes && endTimeMinutes <= bEndTimeMinutes) ||
        (startTimeMinutes <= bStartTimeMinutes && endTimeMinutes >= bEndTimeMinutes)
      );

      if (hasOverlap) {
        conflicts.push({
          bookingId: booking.id,
          bookingNumber: booking.bookingNumber,
          clientName: booking.clientName,
          eventTime: booking.eventTime,
        });
      }
    }
  }

  return conflicts;
};

// Get available time slots for a date
export const getAvailableTimeSlots = async (date) => {
  // Get schedule for the date
  const schedule = await getScheduleByDate(date);

  // Default time slots (9 AM to 8 PM, 1-hour intervals)
  const defaultSlots = [];
  for (let hour = 9; hour <= 20; hour++) {
    defaultSlots.push({
      time: `${hour.toString().padStart(2, '0')}:00`,
      available: true,
    });
  }

  let availableSlots = schedule?.timeSlots || defaultSlots;

  // If date is marked as unavailable, return empty array
  if (schedule && !schedule.isAvailable) {
    return [];
  }

  // Check for existing bookings
  const bookingsRef = collection(fs, "bookings");
  const q = query(
    bookingsRef,
    where("eventDate", "==", date),
    where("status", "in", ["PENDING_DEPOSIT", "CONFIRMED"])
  );

  const snapshot = await getDocs(q);
  const bookings = snapshot.docs.map((doc) => doc.data());

  // Mark booked slots as unavailable
  const slotMap = new Map();
  availableSlots.forEach(slot => {
    slotMap.set(slot.time, { ...slot });
  });

  for (const booking of bookings) {
    if (booking.eventTime) {
      const bookedSlot = slotMap.get(booking.eventTime);
      if (bookedSlot) {
        bookedSlot.available = false;
        bookedSlot.bookingId = booking.id;
        bookedSlot.clientName = booking.clientName;
      }
    }
  }

  return Array.from(slotMap.values());
};

// Bulk update schedules (for setting availability for multiple dates)
export const bulkUpdateSchedules = async (schedules) => {
  const batch = [];
  for (const scheduleData of schedules) {
    const promise = upsertSchedule(scheduleData);
    batch.push(promise);
  }
  await Promise.all(batch);
  return schedules;
};

// ============================================================================
// FAQ MANAGEMENT
// ============================================================================

export const subscribeToFAQs = (callback) => {
  const q = query(collection(fs, "faqs"), orderBy("order", "asc"));
  return onSnapshot(q, (snapshot) => {
    const faqs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    callback(faqs);
  });
};

export const createFAQ = async (faqData) => {
  const faqRef = doc(collection(fs, "faqs"));
  const newFAQ = {
    question: faqData.question,
    answer: faqData.answer,
    category: faqData.category || "general",
    order: faqData.order || 0,
    isActive: faqData.isActive !== undefined ? faqData.isActive : true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(faqRef, newFAQ);
  return { id: faqRef.id, ...newFAQ };
};

export const updateFAQ = async (faqId, updates) => {
  const faqRef = doc(fs, "faqs", faqId);
  await updateDoc(faqRef, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteFAQ = async (faqId) => {
  const faqRef = doc(fs, "faqs", faqId);
  await deleteDoc(faqRef);
};

// ============================================================================
// CONTACT INFO & SOCIAL MEDIA
// ============================================================================

export const getContactInfo = async () => {
  const docRef = doc(fs, "settings", "contact");
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const subscribeToContactInfo = (callback) => {
  const docRef = doc(fs, "settings", "contact");
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  });
};

export const upsertContactInfo = async (contactData) => {
  const docRef = doc(fs, "settings", "contact");
  const data = {
    phone: contactData.phone || "",
    whatsapp: contactData.whatsapp || "",
    email: contactData.email || "",
    address: contactData.address || "",
    city: contactData.city || "",
    region: contactData.region || "",
    postalCode: contactData.postalCode || "",
    mapUrl: contactData.mapUrl || "",
    updatedAt: serverTimestamp(),
  };
  await setDoc(docRef, data, { merge: true });
  return { id: "contact", ...data };
};

export const subscribeToSocialMedia = (callback) => {
  const docRef = doc(fs, "settings", "social");
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback({
        instagram: "",
        facebook: "",
        tiktok: "",
        twitter: "",
        youtube: "",
      });
    }
  });
};

export const upsertSocialMedia = async (socialData) => {
  const docRef = doc(fs, "settings", "social");
  const data = {
    instagram: socialData.instagram || "",
    facebook: socialData.facebook || "",
    tiktok: socialData.tiktok || "",
    twitter: socialData.twitter || "",
    youtube: socialData.youtube || "",
    updatedAt: serverTimestamp(),
  };
  await setDoc(docRef, data, { merge: true });
  return { id: "social", ...data };
};

// ============================================================================
// TERMS & CONDITIONS
// ============================================================================

export const subscribeToTerms = (callback) => {
  const docRef = doc(fs, "settings", "terms");
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      callback(data.items || []);
    } else {
      callback(null);
    }
  });
};

export const upsertTerms = async (termsData) => {
  const docRef = doc(fs, "settings", "terms");
  const data = {
    items: termsData,
    updatedAt: serverTimestamp(),
  };
  await setDoc(docRef, data, { merge: true });
  return { id: "terms", ...data };
};

// ============================================================================
// LANDING PAGE SECTIONS
// ============================================================================

export const subscribeToSectionSettings = (callback) => {
  const docRef = doc(fs, "settings", "sections");
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback({
        hero: true,
        about: true,
        services: true,
        gallery: true,
        certificates: true,
        testimonials: true,
        contact: true,
      });
    }
  });
};

export const upsertSectionSettings = async (settings) => {
  const docRef = doc(fs, "settings", "sections");
  const data = {
    hero: settings.hero !== undefined ? settings.hero : true,
    about: settings.about !== undefined ? settings.about : true,
    services: settings.services !== undefined ? settings.services : true,
    gallery: settings.gallery !== undefined ? settings.gallery : true,
    certificates: settings.certificates !== undefined ? settings.certificates : true,
    testimonials: settings.testimonials !== undefined ? settings.testimonials : true,
    contact: settings.contact !== undefined ? settings.contact : true,
    updatedAt: serverTimestamp(),
  };
  await setDoc(docRef, data, { merge: true });
  return { id: "sections", ...data };
};

// ============================================================================
// EXPORTS
// ============================================================================

export { fs };
export default app;
